// ─────────────────────────────────────────────────────────────────────────
// OcEntrantes — bitácora del correo recepcion@ (28-ago-2026).
// Se llega desde la tarjeta roja "OC por registrar" del Dashboard (no hay ítem
// de menú). El vigía IMAP (backend) revisa recepcion@binimeliz.cl cada 5 min,
// detecta OCs y las cruza contra el ERP; lo que NO calza queda "pendiente" acá
// hasta que alguien registre la OC en /customerOrders y marque "Ya la registré".
// Además registra los correos ENVIADOS desde recepcion@ (📤, solo constancia:
// jamás alertan) — el buzón oficial queda con bitácora entrante y saliente.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import AcusoPreviewModal from "../../components/design-system/AcusoPreviewModal";
import useIsMobile from "../../hooks/useIsMobile";
import useTitle from "../../hooks/useTitle";

const num = { fontVariantNumeric: "tabular-nums" };
const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  // Tabla densa desktop (28-ago)
  th: {
    background: "#1f1f1f", padding: "7px 10px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: {
    padding: "7px 10px", color: "#bbb", borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle",
  },
};

const ESTADO = {
  pendiente:  { label: "Pendiente",  color: "#e74c3c", bg: "#2d1515", border: "#4a2020" },
  registrada: { label: "Registrada", color: "#2ecc71", bg: "#0d2a15", border: "#0d4020" },
  descartada: { label: "Descartada", color: "#8a8a8a", bg: "#1e1e1e", border: "#333" },
};
const TIPO = {
  oc:      { label: "OC",      color: "#f39c12" },
  factura: { label: "Factura", color: "#3498db" },
  // 21-oct-2026: cotización de un proveedor para una solicitud abierta en Compras (la guarda el vigía).
  cotizacionProveedor: { label: "Cotización prov.", color: "#a78bfa" },
  otro:    { label: "Otro",    color: "#8a8a8a" },
};
// Dirección del correo: 📥 llegó al INBOX / 📤 salió desde recepcion@.
// Los docs anteriores al 28-ago no traen el campo → se asumen entrantes.
const DIR = {
  entrante: { label: "📥 Entrante", color: "#3498db", bg: "#12202e", border: "#1e3a55" },
  enviado:  { label: "📤 Enviado",  color: "#9b59b6", bg: "#22152e", border: "#3d2455" },
};

const fmtFecha = f => {
  if (!f) return "—";
  const d = new Date(f);
  return d.toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) + " " +
    d.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" });
};

const OcEntrantes = () => {
  useTitle("Correo recepción");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("pendiente"); // pendiente | todos
  const [dir, setDir] = useState("todos");           // todos | entrante | enviado
  const [tipoFiltro, setTipoFiltro] = useState("todos"); // todos | oc | factura | otro
  const [ocupado, setOcupado] = useState(null);      // id del correo en proceso
  const [acusoMail, setAcusoMail] = useState(null); // correo cuyo acuso se previsualiza

  const cargar = useCallback(() => {
    backend.get("mailEntrante", { params: {
      estado: filtro === "todos" ? "todos" : filtro,
      ...(dir !== "todos" ? { direccion: dir } : {}),
    } })
      .then(r => { setRows(r.data || []); setError(null); })
      .catch(e => setError(e?.response?.data?.message || e.message));
  }, [filtro, dir]);

  useEffect(() => { setRows(null); cargar(); }, [cargar]);

  const marcar = async (mail, estado) => {
    setOcupado(mail._id);
    try {
      await backend.patch(`mailEntrante/${mail._id}/estado`, { estado });
      cargar();
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    }
    setOcupado(null);
  };

  // 28-ago — registrar en el ERP con PRECARGA: navega a /customerOrders con el
  // N° OC, el cliente detectado y el id del correo. Al registrar la OC allá,
  // el correo se marca "registrada" solo (cierre automático del loop del vigía).
  const registrarEnErp = m => navigate(
    "/customerOrders?oc=" + encodeURIComponent(m.ocNumero || "") +
    "&cliente=" + encodeURIComponent(m.clienteDetectado || "") +
    "&mailId=" + m._id
  );

  // Acuso de recibo: abre el modal de PREVIEW; el envío (con confirmación) ocurre
  // dentro del modal (AcusoPreviewModal → POST /mailEntrante/:id/acuso).
  const acusar = mail => setAcusoMail(mail);

  const pendientes = (rows || []).filter(r => r.estado === "pendiente" && r.tipoDetectado === "oc").length;

  // Filtro por tipo (28-ago, client-side sobre lo ya cargado) + vista tabla:
  // en desktop con el filtro "Todos los correos" la lista es tabla densa;
  // móvil y "Pendientes" mantienen las tarjetas.
  const visibles = (rows || []).filter(r => tipoFiltro === "todos" || (r.tipoDetectado || "otro") === tipoFiltro);
  const nPorTipo = k => (rows || []).filter(r => (r.tipoDetectado || "otro") === k).length;
  const esTabla = !isMobile && filtro === "todos";
  const toggleTipo = k => setTipoFiltro(prev => (prev === k ? "todos" : k));

  return (
    <div style={{ padding: "16px 18px", maxWidth: 1200 }}>
      {/* Encabezado */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>📨 Correo recepcion@</span>
        {pendientes > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#e74c3c" }}>
            {pendientes} por registrar
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#8a8a8a", lineHeight: 1.6, marginBottom: 12 }}>
        El vigía revisa <b style={{ color: "#bbb" }}>recepcion@binimeliz.cl</b> cada 5 minutos. Cuando llega una orden de
        compra que <b style={{ color: "#e74c3c" }}>no está registrada en el ERP</b>, aparece acá y Heimdall avisa al
        equipo (días hábiles). El flujo: apretar <b style={{ color: "#2ecc71" }}>"📝 Registrar"</b> en el correo —
        abre{" "}
        <span onClick={() => navigate("/customerOrders")} style={{ color: "#3498db", cursor: "pointer", textDecoration: "underline" }}>
          Órdenes de compra
        </span>{" "}
        con el N° OC y el cliente prellenados, y al registrar la OC el correo se marca solo. Si el correo no
        correspondía (falso positivo), "Descartar". Los correos{" "}
        <b style={{ color: "#9b59b6" }}>📤 enviados</b> desde recepcion@ también quedan acá como bitácora (solo
        constancia, nunca alertan).
      </div>

      {/* Filtros: estado (existente) + dirección (entrante/enviado), compuestos */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        {[["pendiente", "Pendientes"], ["todos", "Todos los correos"]].map(([k, l]) => (
          <button key={k} onClick={() => setFiltro(k)} className="chip-touch no-touch-min t-02"
            style={{ background: filtro === k ? "#3a1114" : "#1e1e1e", color: filtro === k ? "#ff8a8f" : "#999",
              border: `1px solid ${filtro === k ? "#e30613" : "#333"}`, borderRadius: 6, padding: "6px 12px", fontSize: 11, cursor: "pointer" }}>
            {l}
          </button>
        ))}
        <span style={{ width: 1, height: 18, background: "#333", margin: "0 4px" }} />
        {[["todos", "Todos"], ["entrante", "📥 Entrantes"], ["enviado", "📤 Enviados"]].map(([k, l]) => (
          <button key={k}
            onClick={() => {
              setDir(k);
              // Los enviados jamás quedan "pendiente": si el filtro de estado
              // estaba en Pendientes, se abre a Todos para no mostrar vacío.
              if (k === "enviado" && filtro === "pendiente") setFiltro("todos");
            }}
            className="chip-touch no-touch-min t-02"
            style={{ background: dir === k ? "#12202e" : "#1e1e1e", color: dir === k ? "#5dade2" : "#999",
              border: `1px solid ${dir === k ? "#3498db" : "#333"}`, borderRadius: 6, padding: "6px 12px", fontSize: 11, cursor: "pointer" }}>
            {l}
          </button>
        ))}
        <span style={{ width: 1, height: 18, background: "#333", margin: "0 4px" }} />
        {/* Chips por tipo detectado (28-ago): OC / Factura / Otro con contador */}
        {["oc", "factura", "cotizacionProveedor", "otro"].map(k => {
          const t = TIPO[k];
          const activo = tipoFiltro === k;
          return (
            <button key={k} onClick={() => toggleTipo(k)} className="chip-touch no-touch-min t-02"
              title={activo ? "Quitar el filtro de tipo" : `Filtrar: solo tipo ${t.label}`}
              style={{ background: activo ? `${t.color}22` : "#1e1e1e", color: activo ? t.color : "#999",
                border: `1px solid ${activo ? t.color : "#333"}`, borderRadius: 6, padding: "6px 12px",
                fontSize: 11, fontWeight: activo ? 700 : 400, cursor: "pointer", ...num }}>
              {t.label} ({nPorTipo(k)}){activo ? " ✕" : ""}
            </button>
          );
        })}
      </div>

      {error && <div style={{ color: "#e74c3c", fontSize: 12, marginBottom: 10 }}>Error: {error}</div>}
      <AcusoPreviewModal mailId={acusoMail?._id} onClose={() => setAcusoMail(null)} onSent={cargar} />

      {!rows && !error && <div style={{ color: "#8a8a8a", fontSize: 12 }}>Cargando correos…</div>}

      {/* Lista: tabla densa en desktop con "Todos los correos"; tarjetas en
          móvil y en el filtro "Pendientes" (28-ago) */}
      {esTabla && rows && visibles.length > 0 ? (
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "105px" }} /> {/* Fecha */}
              <col style={{ width: "42px" }} />  {/* Dir */}
              <col style={{ width: "70px" }} />  {/* Tipo */}
              <col style={{ width: "90px" }} />  {/* OC */}
              <col style={{ width: "190px" }} /> {/* Remitente/Para */}
              <col />                            {/* Asunto */}
              <col style={{ width: "95px" }} />  {/* Estado */}
              <col style={{ width: "230px" }} /> {/* Acciones */}
            </colgroup>
            <thead>
              <tr>
                <th style={S.th}>Fecha</th>
                <th style={S.th} title="Dirección: entrante o enviado">Dir</th>
                <th style={S.th}>Tipo</th>
                <th style={S.th}>OC</th>
                <th style={S.th}>Remitente / Para</th>
                <th style={S.th}>Asunto</th>
                <th style={S.th}>Estado</th>
                <th style={S.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((mail, i) => {
                const est = ESTADO[mail.estado] || ESTADO.descartada;
                const tipo = TIPO[mail.tipoDetectado] || TIPO.otro;
                const esEnviado = mail.direccion === "enviado";
                const d = esEnviado ? DIR.enviado : DIR.entrante;
                const esPendiente = mail.estado === "pendiente";
                const trabajando = ocupado === mail._id;
                return (
                  <tr key={mail._id} className="bini-row-hover"
                    style={{ background: esPendiente ? "#241416" : i % 2 === 0 ? "#232323" : "#212121" }}>
                    <td style={{ ...S.td, ...num }}>{fmtFecha(mail.fecha)}</td>
                    <td style={{ ...S.td, textAlign: "center" }} title={d.label}>{esEnviado ? "📤" : "📥"}</td>
                    <td style={S.td}>
                      <span
                        onClick={() => toggleTipo(mail.tipoDetectado || "otro")}
                        title={`Filtrar: solo tipo ${tipo.label}`}
                        style={{ fontSize: 10, color: tipo.color, border: `1px solid ${tipo.color}55`, borderRadius: 4, padding: "1px 6px", fontWeight: 700, cursor: "pointer" }}>
                        {tipo.label}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: "#f39c12", fontWeight: 800, ...num }}>{mail.ocNumero || "—"}</td>
                    <td style={S.td} title={esEnviado ? mail.destinatario : mail.remitente}>
                      {esEnviado ? (mail.destinatario || "—") : (mail.remitente || "—")}
                    </td>
                    <td style={{ ...S.td, color: "#fff", fontWeight: 700 }}
                      title={`${mail.asunto || "(sin asunto)"}${mail.resumen ? `\n\n${mail.resumen}` : ""}${(mail.adjuntos || []).length ? `\n📎 ${mail.adjuntos.map(a => a.nombre).join(", ")}` : ""}`}>
                      {mail.asunto || "(sin asunto)"}
                      {mail.registradaEn && (
                        <span style={{ color: "#2ecc71", fontWeight: 400, fontSize: 11, ...num }}> · ✔ OC N° {mail.registradaEn.number}</span>
                      )}
                      {mail.notas && <span style={{ color: "#f39c12", fontWeight: 400, fontSize: 11 }}> · ⚠ {mail.notas}</span>}
                    </td>
                    <td style={{ ...S.td, overflow: "visible" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: est.color, background: est.bg, border: `1px solid ${est.border}`, borderRadius: 5, padding: "2px 8px", whiteSpace: "nowrap" }}>
                        {est.label}
                      </span>
                    </td>
                    <td style={{ ...S.td, overflow: "visible" }}>
                      <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                        {!esEnviado && mail.estado !== "descartada" && (
                          mail.acuso && mail.acuso.enviado ? (
                            <span style={{ fontSize: 10, color: "#2ecc71", whiteSpace: "nowrap", ...num }} title={`Acusado el ${fmtFecha(mail.acuso.fecha)}`}>✓ Acusado</span>
                          ) : (
                            <button onClick={() => acusar(mail)} disabled={trabajando} className="t-02"
                              style={{ background: "#152030", color: "#3498db", border: "1px solid #1e3a55",
                                borderRadius: 5, padding: "3px 8px", fontSize: 10, fontWeight: 700,
                                cursor: trabajando ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                              ✉️ Acusar
                            </button>
                          )
                        )}
                        {esPendiente && (
                          <>
                            <button onClick={() => registrarEnErp(mail)} disabled={trabajando} className="t-02"
                              title="Abre el formulario de registrar OC con el N° y el cliente prellenados. Al registrarla, este correo se marca solo."
                              style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #2ecc71",
                                borderRadius: 5, padding: "3px 8px", fontSize: 10, fontWeight: 700,
                                cursor: trabajando ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                              📝 Registrar
                            </button>
                            <button onClick={() => marcar(mail, "registrada")} disabled={trabajando} className="t-02"
                              title="Marcar como registrada sin ir al formulario (si la OC ya está en el ERP)"
                              style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020",
                                borderRadius: 5, padding: "3px 8px", fontSize: 10, fontWeight: 700,
                                cursor: trabajando ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                              ✔ Ya la registré
                            </button>
                            <button onClick={() => marcar(mail, "descartada")} disabled={trabajando} className="t-02"
                              style={{ background: "#1e1e1e", color: "#999", border: "1px solid #333",
                                borderRadius: 5, padding: "3px 8px", fontSize: 10, fontWeight: 700,
                                cursor: trabajando ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                              Descartar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {visibles.map(mail => {
          const est = ESTADO[mail.estado] || ESTADO.descartada;
          const tipo = TIPO[mail.tipoDetectado] || TIPO.otro;
          const esEnviado = mail.direccion === "enviado";
          const d = esEnviado ? DIR.enviado : DIR.entrante;
          const esPendiente = mail.estado === "pendiente";
          return (
            <div key={mail._id} style={{ ...S.card, border: `1px solid ${esPendiente ? "#5a2020" : "#2e2e2e"}`, background: esPendiente ? "#241416" : "#232323" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: d.color, background: d.bg, border: `1px solid ${d.border}`, borderRadius: 4, padding: "1px 6px" }}>
                      {d.label}
                    </span>
                    <span
                      onClick={() => toggleTipo(mail.tipoDetectado || "otro")}
                      title={`Filtrar: solo tipo ${tipo.label}`}
                      style={{ fontSize: 10, color: tipo.color, border: `1px solid ${tipo.color}55`, borderRadius: 4, padding: "1px 6px", fontWeight: 700, cursor: "pointer" }}>
                      {tipo.label}
                    </span>
                    {mail.ocNumero && (
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#f39c12", ...num }}>OC {mail.ocNumero}</span>
                    )}
                    {mail.clienteDetectado && <span style={{ fontSize: 11, color: "#bbb" }}>{mail.clienteDetectado}</span>}
                    <span style={{ fontSize: 10, color: "#777", ...num }}>{fmtFecha(mail.fecha)}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mail.asunto || "(sin asunto)"}
                  </div>
                  <div style={{ fontSize: 11, color: "#8a8a8a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {esEnviado ? `Para: ${mail.destinatario || "—"}` : `De: ${mail.remitente || "—"}`}
                  </div>
                  {mail.resumen && (
                    <div style={{ fontSize: 11, color: "#999", marginTop: 4, lineHeight: 1.5,
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {mail.resumen}
                    </div>
                  )}
                  {(mail.adjuntos || []).length > 0 && (
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                      {mail.adjuntos.map((a, i) => (
                        <span key={i} style={{ fontSize: 10, color: "#3498db", border: "1px solid #3498db44", borderRadius: 4, padding: "1px 6px" }}>
                          📎 {a.nombre}
                        </span>
                      ))}
                    </div>
                  )}
                  {mail.notas && <div style={{ fontSize: 10, color: "#f39c12", marginTop: 4 }}>⚠ {mail.notas}</div>}
                  {mail.registradaEn && (
                    <div style={{ fontSize: 10, color: "#2ecc71", marginTop: 4, ...num }}>
                      ✔ En el ERP: OC interna N° {mail.registradaEn.number}{mail.registradaEn.clientOCNumber ? ` · OC cliente ${mail.registradaEn.clientOCNumber}` : ""}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: est.color, background: est.bg, border: `1px solid ${est.border}`, borderRadius: 5, padding: "3px 8px" }}>
                    {est.label}
                  </span>
                  {/* Acuso solo para correos entrantes: uno enviado por nosotros no se acusa. */}
                  {!esEnviado && mail.estado !== "descartada" && (
                    mail.acuso && mail.acuso.enviado ? (
                      <span style={{ fontSize: 10, color: "#2ecc71", ...num }}>
                        ✓ Acusado el {fmtFecha(mail.acuso.fecha)}
                      </span>
                    ) : (
                      <button
                        onClick={() => acusar(mail)}
                        disabled={ocupado === mail._id}
                        className="chip-touch no-touch-min t-02"
                        style={{ background: "#152030", color: "#3498db", border: "1px solid #1e3a55",
                          borderRadius: 6, padding: "6px 10px", fontSize: 11, fontWeight: 700,
                          cursor: ocupado === mail._id ? "not-allowed" : "pointer" }}>
                        ✉️ Acuso de recibo
                      </button>
                    )
                  )}
                  {esPendiente && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button
                        onClick={() => registrarEnErp(mail)}
                        disabled={ocupado === mail._id}
                        className="chip-touch no-touch-min t-02"
                        title="Abre el formulario de registrar OC con el N° y el cliente prellenados. Al registrarla, este correo se marca solo."
                        style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #2ecc71",
                          borderRadius: 6, padding: "6px 10px", fontSize: 11, fontWeight: 700,
                          cursor: ocupado === mail._id ? "not-allowed" : "pointer" }}>
                        📝 Registrar
                      </button>
                      <button
                        onClick={() => marcar(mail, "registrada")}
                        disabled={ocupado === mail._id}
                        className="chip-touch no-touch-min t-02"
                        title="Marcar como registrada sin ir al formulario (si la OC ya está en el ERP)"
                        style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020",
                          borderRadius: 6, padding: "6px 10px", fontSize: 11, fontWeight: 700,
                          cursor: ocupado === mail._id ? "not-allowed" : "pointer" }}>
                        ✔ Ya la registré
                      </button>
                      <button
                        onClick={() => marcar(mail, "descartada")}
                        disabled={ocupado === mail._id}
                        className="chip-touch no-touch-min t-02"
                        style={{ background: "#1e1e1e", color: "#999", border: "1px solid #333",
                          borderRadius: 6, padding: "6px 10px", fontSize: 11, fontWeight: 700,
                          cursor: ocupado === mail._id ? "not-allowed" : "pointer" }}>
                        Descartar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {rows && visibles.length === 0 && (
          <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
            {tipoFiltro !== "todos" && rows.length > 0
              ? `Sin correos de tipo ${TIPO[tipoFiltro]?.label || tipoFiltro} con estos filtros.`
              : filtro === "pendiente"
                ? "Sin OC pendientes de registrar — todo lo que llegó a recepcion@ está en el ERP. 👌"
                : "Aún no hay correos detectados en recepcion@."}
          </div>
        )}
      </div>
      )}

      <div style={{ fontSize: 10, color: "#666", marginTop: 12, lineHeight: 1.6 }}>
        Nada se borra: cada correo queda como constancia de que la OC llegó. Las facturas/DTE se registran sin
        alerta (las concilia el módulo de Compras/SII). Si el vigía aparece dormido en los logs, falta la App
        Password de recepcion@ en Configuración &gt; General.
      </div>
    </div>
  );
};

export default OcEntrantes;
